/**
 * Teen Reset Page
 * Quick breathing/calming exercises including exam prep reset.
 * Uses TeenThemeContext for customizable look and feel.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Wind,
  Brain,
  Users,
  Flame,
  Moon,
  X,
  Check
} from "lucide-react";
import axios from "axios";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";
import BreathOrb from "../../components/BreathOrb";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  wind: Wind,
  brain: Brain,
  users: Users,
  flame: Flame,
  moon: Moon
};

const TeenResetContent = () => {
  const navigate = useNavigate();
  const { theme, currentPalette } = useTeenTheme();
  const [resets, setResets] = useState([]);
  const [selectedReset, setSelectedReset] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle, inhale, hold, exhale
  const [breathCount, setBreathCount] = useState(0);
  const [guidanceIndex, setGuidanceIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  // Theme colors
  const colors = {
    base: theme?.colors?.base || '#0f0f23',
    primary: currentPalette?.primary || theme?.colors?.primary || '#818CF8',
    accent: currentPalette?.accent || theme?.colors?.accent || '#F472B6',
    textPrimary: theme?.colors?.textPrimary || '#ffffff',
    textSecondary: theme?.colors?.textSecondary || 'rgba(255,255,255,0.6)',
  };
  
  const timersRef = useRef([]);
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadResets();
    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  const loadResets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/resets`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setResets(res.data.resets);
    } catch (err) {
      console.error("Error loading resets:", err);
    }
  };

  const handleSelectReset = (reset) => {
    setSelectedReset(reset);
    setIsReady(true);
    setPhase("idle");
    setBreathCount(0);
    setGuidanceIndex(0);
    setIsComplete(false);
  };

  const addTimer = (callback, delay) => {
    const t = setTimeout(callback, delay);
    timersRef.current.push(t);
    return t;
  };

  const handleStart = () => {
    setIsReady(false);
    runBreathingCycle();
  };

  const runBreathingCycle = () => {
    const pattern = selectedReset.breathing_pattern;
    const totalCycles = Math.floor(selectedReset.duration / (pattern.reduce((a, b) => a + b, 0)));
    let currentCycle = 0;
    
    const runCycle = () => {
      if (currentCycle >= totalCycles) {
        // Complete
        completeReset();
        return;
      }
      
      // Update guidance
      const guidanceLen = selectedReset.guidance.length;
      setGuidanceIndex(Math.min(currentCycle, guidanceLen - 1));
      
      // Inhale
      setPhase("inhale");
      addTimer(() => {
        // Hold (if 3-part pattern)
        if (pattern.length === 3) {
          setPhase("hold");
          addTimer(() => {
            // Exhale
            setPhase("exhale");
            addTimer(() => {
              currentCycle++;
              setBreathCount(currentCycle);
              runCycle();
            }, pattern[2] * 1000);
          }, pattern[1] * 1000);
        } else {
          // 4-part box breathing
          setPhase("hold");
          addTimer(() => {
            setPhase("exhale");
            addTimer(() => {
              setPhase("hold");
              addTimer(() => {
                currentCycle++;
                setBreathCount(currentCycle);
                runCycle();
              }, pattern[3] * 1000);
            }, pattern[2] * 1000);
          }, pattern[1] * 1000);
        }
      }, pattern[0] * 1000);
    };
    
    runCycle();
  };

  const completeReset = async () => {
    setIsComplete(true);
    
    try {
      await axios.post(
        `${API_URL}/api/teen/resets/${selectedReset.id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (err) {
      console.error("Error logging reset:", err);
    }
  };

  const handleClose = () => {
    timersRef.current.forEach(t => clearTimeout(t));
    setSelectedReset(null);
    setIsReady(false);
    setIsComplete(false);
    setPhase("idle");
  };

  // Reset selection grid
  if (!selectedReset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
        {/* Header */}
        <div className="p-4">
          <button
            onClick={() => navigate("/teen")}
            className="text-white/60 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 pt-2 pb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Quick Reset</h1>
          <p className="text-white/60">Calm your mind in seconds</p>
        </div>

        {/* Resets */}
        <div className="px-6 pb-8 space-y-4">
          {resets.map((reset, index) => {
            const Icon = ICONS[reset.icon] || Wind;
            return (
              <motion.button
                key={reset.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelectReset(reset)}
                className={`w-full p-5 rounded-2xl bg-gradient-to-r ${reset.color} shadow-xl flex items-center gap-4 text-left`}
                data-testid={`reset-${reset.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-0.5">{reset.name}</h3>
                  <p className="text-white/80 text-sm">{reset.description}</p>
                </div>
                <div className="text-white/60 text-sm">
                  {Math.floor(reset.duration / 60)}:{(reset.duration % 60).toString().padStart(2, '0')}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  const Icon = ICONS[selectedReset.icon] || Wind;

  // Ready screen
  if (isReady) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`min-h-screen bg-gradient-to-b ${selectedReset.color} flex flex-col items-center justify-center px-6`}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          <X size={24} />
        </button>

        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-24 h-24 rounded-2xl bg-white/20 flex items-center justify-center mb-8"
        >
          <Icon size={40} className="text-white" />
        </motion.div>

        <h1 className="text-2xl font-bold text-white mb-3">{selectedReset.name}</h1>
        <p className="text-white/80 text-center mb-8 max-w-sm">{selectedReset.description}</p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStart}
          className="px-10 py-4 rounded-xl bg-white/20 text-white font-bold text-lg shadow-xl flex items-center gap-3"
          data-testid="start-reset-btn"
        >
          <Wind size={24} />
          <span>Begin Reset</span>
        </motion.button>
      </motion.div>
    );
  }

  // Completion screen
  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`min-h-screen bg-gradient-to-b ${selectedReset.color} flex flex-col items-center justify-center px-6`}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mb-8"
        >
          <Check size={48} className="text-white" />
        </motion.div>

        <h1 className="text-2xl font-bold text-white mb-3">Reset Complete</h1>
        <p className="text-white/80 text-center mb-8 max-w-sm">
          {selectedReset.guidance[selectedReset.guidance.length - 1]}
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleClose}
          className="px-10 py-4 rounded-xl bg-white/20 text-white font-semibold"
        >
          Done
        </motion.button>
      </motion.div>
    );
  }

  // Active breathing
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`min-h-screen bg-gradient-to-b ${selectedReset.color} flex flex-col`}
    >
      {/* Header */}
      <div className="p-4 flex justify-end">
        <button
          onClick={handleClose}
          className="text-white/60 hover:text-white"
        >
          <X size={24} />
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Breathing orb — canonical soft gold BreathOrb */}
        <div className="relative mb-8 flex items-center justify-center">
          <BreathOrb size={208} tone="gold" phase={phase === "idle" ? "rest" : phase} data-testid="teen-reset-breath-orb" />
        </div>

        {/* Phase instruction */}
        <AnimatePresence mode="wait">
          <motion.p
            key={phase}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-white text-2xl font-medium mb-4"
          >
            {phase === "inhale" && "Breathe in..."}
            {phase === "hold" && "Hold..."}
            {phase === "exhale" && "Release..."}
            {phase === "idle" && "Get ready..."}
          </motion.p>
        </AnimatePresence>

        {/* Guidance text */}
        <AnimatePresence mode="wait">
          <motion.p
            key={guidanceIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-white/70 text-center max-w-sm text-lg"
          >
            {selectedReset.guidance[guidanceIndex]}
          </motion.p>
        </AnimatePresence>

        {/* Breath counter */}
        <div className="mt-8 text-white/50 text-sm">
          Breath {breathCount + 1}
        </div>
      </div>
    </motion.div>
  );
};

// Main component wraps with theme provider
const TeenResetPage = () => {
  return (
    <TeenThemeProvider>
      <TeenResetContent />
    </TeenThemeProvider>
  );
};

export default TeenResetPage;
